/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { Theme } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';

import {
  publishProctorConfig,
  publishProctorConfigOverride,
} from 'bundles/author-assess-common/actions/ProctorConfigActions';
import { useCreateProctorConfigOverride } from 'bundles/author-assess-common/data/proctoring/useCreateProctorConfigOverride';
import type { CreateProctorConfigOverrideProps } from 'bundles/author-assess-common/data/proctoring/useCreateProctorConfigOverride';
import { getAssessmentId } from 'bundles/author-assess-common/lib/Utils';
import SaveActionConfirmModal from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/SaveActionConfirmModal';
import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import {
  programmingIseContextStateHasChanges,
  useProgrammingProctoringConfigurationContext,
} from 'bundles/author-assignment-grading/components/programming/context/ProgrammingProctoringConfigurationContext';
import type { ProgrammingProctoringConfigurationContextType } from 'bundles/author-assignment-grading/components/programming/context/ProgrammingProctoringConfigurationContext';
import type { SaveNotificationType } from 'bundles/author-assignment-grading/constants/SaveNotificationTypes';
import SaveNotificationTypes from 'bundles/author-assignment-grading/constants/SaveNotificationTypes';
import { useGroupItemGrades } from 'bundles/author-assignment-grading/utils/withGroupItemGrades';
import type { UseGroupItemGradesResult } from 'bundles/author-assignment-grading/utils/withGroupItemGrades';
import type Item from 'bundles/author-course/models/Item';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import type { UserItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { TaskActionsRenderProps } from 'bundles/compound-assessments/components/api/StaffTaskActions';
import { useStepStateContext } from 'bundles/compound-assessments/components/local-state/step-state/StepStateContext';
import type { StepStateContextType } from 'bundles/compound-assessments/components/local-state/step-state/StepStateContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { LoadingSpinner } from 'bundles/grading';
import { useProgrammingCreateAttemptOverride } from 'bundles/programming/api/useProgrammingCreateAttemptOverride';
import type { WithProgrammingCreateAttemptOverrideProps } from 'bundles/programming/api/useProgrammingCreateAttemptOverride';
import { saveOverriddenSessionToServer } from 'bundles/student-support-dashboard/actions/LearnerScheduleActions';

import _t from 'i18n!nls/author-assignment-grading';

type InputProps = {
  item: Item;
  sessionItem: SessionItem;
  userId?: string | number;
  grade?: UserItemGrade;
  team?: Team;
  showConfirmationOnSave: boolean;
  currentRoute: { name: string };
  gradingTabSelected: boolean;
  gradingChange?: null | (() => Promise<void>);
  clearGradingChange?: (key: string) => void;
  isSentToGradebook?: boolean;
  areRubricsComplete?: boolean;
  newGradeFromRubrics?: number;
  isReadOnly?: boolean;
  isGradeAutosaveEnabled?: boolean;
  graderButtonTextOverride?: { text: string; name: string };

  // callback notifies when "saving" status changes
  setSavingStatus?: (savingStatus: boolean) => void;
  // an alternative way to provide `showNotification`/`notificationType` callback when this component is used outside of a legacy react context provided by `GraderModuleSidebar`
  overrideShowNotification?: (saveNotification: SaveNotificationType) => void;
  inlineButton?: boolean;
  showSavingIcon?: boolean;
  cssOverride?: Interpolation<Theme>;
};

type PropsFromStepStateContext = {
  stepState?: TaskActionsRenderProps['stepState'];
  setStepState?: TaskActionsRenderProps['setStepState'];
};

type PropsFromWithProgrammingProctoringConfigurationContext = ProgrammingProctoringConfigurationContextType;

type PropsFromWithProgrammingCreateAttemptOverride = WithProgrammingCreateAttemptOverrideProps;

export type PropsFromStores = {
  courseId: string;
  modifiedSession?: Record<string, unknown>;
  hasSessionSaveError: boolean;
  hasValidSessionDates: boolean;
  isSessionSaveSuccess: boolean;
  modifiedProctorConfig?: boolean;
  hasProctorConfigSaveError: boolean;
  proctorConfigDraftId?: string;
  groupId: string;
};

export type Props = InputProps &
  Pick<UseGroupItemGradesResult, 'groupItemGrades' | 'refetchGroupItemGrades'> &
  PropsFromWithProgrammingCreateAttemptOverride &
  CreateProctorConfigOverrideProps &
  PropsFromStores & {
    programmingProctorConfigContext: PropsFromWithProgrammingProctoringConfigurationContext;
    stepStateCtx: StepStateContextType;
  };

type State = {
  saveInProgress: boolean;
  showConfirmationModal: boolean;
};

const styles = {
  root: () =>
    css({
      width: '100%',
      flex: 'initial',
      '.rc-ConfirmChanges': {
        width: '100%',
      },
      '.save-btn': {
        height: '48px',
        width: '100%',
        marginBottom: 'var(--cds-spacing-200)',
      },
    }),
  inlineRoot: () =>
    css({
      display: 'inline',
    }),
};

export class GraderModuleSaveButton extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof GraderModuleSaveButton.contextTypes>;

  state = {
    saveInProgress: false,
    showConfirmationModal: false,
  };

  componentDidUpdate = (prevProps: Props) => {
    const {
      hasSessionSaveError,
      hasProctorConfigSaveError,
      proctorConfigDraftId,
      modifiedProctorConfig,
      modifiedSession,
      isSessionSaveSuccess,
      overrideShowNotification,
    } = this.props;
    const { showNotification } = this.context;

    // if the overrideShowNotification is provided, then that callback will be used instead
    const showNotificationSetter = overrideShowNotification || showNotification;

    // absence of a modified session + isSessionSaveSuccess being `true`  means a successful save on server
    const sessionSaveSuccess =
      prevProps.modifiedSession && !modifiedSession && !prevProps.isSessionSaveSuccess && isSessionSaveSuccess;

    if (!prevProps.hasSessionSaveError && hasSessionSaveError) {
      showNotificationSetter(SaveNotificationTypes.ERROR);
      this.setState(() => ({ saveInProgress: false }));
    } else if (!prevProps.hasProctorConfigSaveError && hasProctorConfigSaveError) {
      showNotificationSetter(SaveNotificationTypes.ERROR);
      this.setState(() => ({ saveInProgress: false }));
    } else if (sessionSaveSuccess) {
      showNotificationSetter(SaveNotificationTypes.SUCCESS);
      this.setState(() => ({ saveInProgress: false }));
    } else if (
      prevProps.modifiedProctorConfig &&
      !modifiedProctorConfig &&
      proctorConfigDraftId &&
      proctorConfigDraftId === prevProps.proctorConfigDraftId
    ) {
      showNotificationSetter(SaveNotificationTypes.SUCCESS);
      this.setState(() => ({ saveInProgress: false }));
    }
  };

  handleSave = async () => {
    const { gradingTabSelected, showConfirmationOnSave } = this.props;
    // Set save in progress then trigger the save so that the UI string updates
    this.setState({ saveInProgress: true }, async () => {
      if (gradingTabSelected && showConfirmationOnSave) {
        this.setState({ showConfirmationModal: true });
      } else if (gradingTabSelected) {
        await this.handleGradingSave();
      } else {
        await this.handleItemSettingExceptionsSave();
      }
    });
  };

  handleGradingSave = async () => {
    const {
      gradingChange,
      refetchGroupItemGrades,
      stepStateCtx: { setStepState },
    } = this.props;
    const { showNotification } = this.context;
    this.setState({ showConfirmationModal: false });
    if (gradingChange) {
      try {
        setStepState?.({ isSubmitting: true });

        await gradingChange?.();
        await refetchGroupItemGrades?.();
        showNotification(SaveNotificationTypes.SUCCESS);
      } catch (err) {
        if ((err && err.status && err.status === 409) || err?.networkError?.statusCode === 409) {
          showNotification(SaveNotificationTypes.CONFLICT);
        } else {
          showNotification(SaveNotificationTypes.ERROR);
        }
      }
    }
    this.setState({ saveInProgress: false });
    setStepState?.({ isSubmitting: false });
  };

  handleItemSettingExceptionsSave = () => {
    const { item, userId, team, modifiedSession, createProctorConfigOverride, overrideShowNotification } = this.props;
    const { showNotification, executeAction } = this.context;

    // if the overrideShowNotification is provided, then that callback will be used instead
    const showNotificationSetter = overrideShowNotification || showNotification;

    if (!team) {
      if (item.standardProctorConfigurationId && !item.isPeer) {
        executeAction(publishProctorConfig, {});
      } else if (item.isPeer && createProctorConfigOverride) {
        executeAction(publishProctorConfigOverride, {
          context: {
            createProctorConfigOverride,
          },
        });
      } else if (item.isGradedProgramming) {
        this.handleProgrammingItemSettingExceptionsSave();
      }
    }

    // has session changes to save
    if (modifiedSession) {
      try {
        // for team assignments, loop over all members, as the BE does not support doing this w/ a single call
        if (team) {
          // PARTNER-17844, for a team, only load updated learner item schedule for the first member is enough
          // because we only load and display the learner item schedule of the first team member in AssignmentGradingUserSubmissionWrapper
          const [firstMemberInTeam, ...restMembersInTeam] = team.members;
          this.saveSessionOverridesForLearner(firstMemberInTeam, true);
          restMembersInTeam.forEach((member) => this.saveSessionOverridesForLearner(member));
        } else if (userId) {
          this.saveSessionOverridesForLearner(userId, true);
        }
      } catch {
        showNotificationSetter(SaveNotificationTypes.ERROR);
      }
    }
  };

  handleProgrammingItemSettingExceptionsSave = async () => {
    const { item, courseId, userId, programmingProctorConfigContext, programmingCreateAttemptOverride } = this.props;

    const { programmingIseContextState, setProgrammingIseContextState, attemptSummary, refetchAttemptSummary } =
      programmingProctorConfigContext;
    // only checking attemptSummary to satisfy the type checker
    if (attemptSummary && !programmingIseContextStateHasChanges(attemptSummary, programmingIseContextState)) {
      return;
    }

    const { showNotification } = this.context;

    const { allowedDurationSeconds, allowedSubmissions, startAttemptNow } = programmingIseContextState;
    const payload = {
      userId: `${userId}`,
      courseId,
      itemId: item.id,
      startAttemptNow,
      timeLimitSeconds: allowedDurationSeconds,
      maxSubmissions: allowedSubmissions,
    };

    try {
      // Mark save in progress
      this.setState({ saveInProgress: true }); // This might have already been set, but doing it again just in case.
      setProgrammingIseContextState({ isSaving: true });

      // Wait for this to complete before running any success handler or the `finally` below.
      await programmingCreateAttemptOverride?.(payload);

      // Refresh the attempt summary
      await refetchAttemptSummary?.();

      showNotification(SaveNotificationTypes.SUCCESS);
      this.setState({ saveInProgress: false });
      setProgrammingIseContextState({
        isSaving: false,
      });
    } catch (err) {
      setProgrammingIseContextState({ isSaving: false });
      if ((err && err.status && err.status === 409) || err?.networkError?.statusCode === 409) {
        showNotification(SaveNotificationTypes.CONFLICT);
      } else {
        showNotification(SaveNotificationTypes.ERROR);
      }
    } finally {
      // Unmark save in progress.
      this.setState({ saveInProgress: false });
      setProgrammingIseContextState({
        isSaving: false,
      });
    }
  };

  cancelGradingSave = () => this.setState({ saveInProgress: false, showConfirmationModal: false });

  saveSessionOverridesForLearner = (userId: $TSFixMe, shouldLoadLearnerItemSchedule = false) => {
    const { sessionItem } = this.props;
    const { executeAction } = this.context;

    executeAction(saveOverriddenSessionToServer, {
      session: sessionItem.overriddenSession,
      groupId: sessionItem.groupId,
      userId,
      shouldLoadLearnerItemSchedule,
    });
  };

  getButtonText() {
    const { gradingTabSelected, isGradeAutosaveEnabled, graderButtonTextOverride } = this.props;

    if (graderButtonTextOverride) {
      return graderButtonTextOverride.text;
    }
    const { saveInProgress, showConfirmationModal } = this.state;

    if (isGradeAutosaveEnabled && gradingTabSelected) {
      return saveInProgress && !showConfirmationModal ? _t('Submitting...') : _t('Submit to gradebook');
    }

    return saveInProgress && !showConfirmationModal ? _t('Saving') : _t('Save');
  }

  getButtonName() {
    const {
      gradingTabSelected,
      isGradeAutosaveEnabled,
      currentRoute: { name = '' } = {},
      graderButtonTextOverride,
    } = this.props;

    if (graderButtonTextOverride) {
      return graderButtonTextOverride.name;
    }

    if (isGradeAutosaveEnabled && gradingTabSelected) {
      return 'Submit to gradebook button';
    }

    if (name === 'assignment_grading_user_submission') {
      return 'Grading save button';
    }

    if (name === 'assignment_grading_user_exception') {
      return 'Exception save button';
    }

    return 'Save button';
  }

  render() {
    const {
      modifiedSession,
      modifiedProctorConfig,
      gradingTabSelected,
      gradingChange,
      hasValidSessionDates,
      sessionItem,
      grade,
      isSentToGradebook,
      areRubricsComplete,
      newGradeFromRubrics,
      isReadOnly,
      stepStateCtx,
      isGradeAutosaveEnabled,

      setSavingStatus,
      inlineButton = false,
      showSavingIcon = false,
      cssOverride,
      programmingProctorConfigContext,
    } = this.props;
    const { stepState } = stepStateCtx;

    const { programmingIseContextState, attemptSummary } = programmingProctorConfigContext;

    const { saveInProgress, showConfirmationModal } = this.state;

    const isProgrammingIseChanged =
      attemptSummary && programmingIseContextStateHasChanges(attemptSummary, programmingIseContextState);

    const hasChange = gradingTabSelected
      ? gradingChange !== null
      : (hasValidSessionDates && !!modifiedSession) || !!modifiedProctorConfig || isProgrammingIseChanged;

    const saveOrSubmitInProgress =
      gradingTabSelected && isGradeAutosaveEnabled
        ? stepState?.isAutoSaving ||
          stepState?.isSaving ||
          stepState?.isSubmitting ||
          programmingIseContextState?.isSaving ||
          saveInProgress
        : saveInProgress;

    // set saving status for quick grader, otherwise ignore for legacy
    setSavingStatus?.(saveOrSubmitInProgress);

    return (
      <div data-testid="GraderModuleSaveButton" css={[inlineButton ? styles.inlineRoot : styles.root]}>
        <Button
          disabled={saveOrSubmitInProgress || !hasChange || isReadOnly}
          onClick={this.handleSave}
          className="save-btn"
          name={this.getButtonName()}
          size="medium"
          css={cssOverride}
          icon={showSavingIcon && saveOrSubmitInProgress ? <LoadingSpinner /> : undefined}
          iconPosition="before"
        >
          {this.getButtonText()}
        </Button>
        {showConfirmationModal && grade && (
          <SaveActionConfirmModal
            grade={grade}
            sessionId={sessionItem.sessionId}
            onCancel={this.cancelGradingSave}
            onConfirm={this.handleGradingSave}
            isSentToGradebook={isSentToGradebook}
            areRubricsComplete={areRubricsComplete}
            newGradeFromRubrics={newGradeFromRubrics}
          />
        )}
      </div>
    );
  }
}

export type WrapperProps = InputProps & PropsFromStepStateContext & PropsFromStores;

export const GraderModuleSaveButtonWrapper = (props: WrapperProps) => {
  const { userId, item, groupId } = props;

  const assessmentId = getAssessmentId(item)?.split('@')[0];

  const { createProctorConfigOverride } = useCreateProctorConfigOverride({ userId, assessmentId });

  const { programmingCreateAttemptOverride } = useProgrammingCreateAttemptOverride();

  const programmingProctorConfigContext = useProgrammingProctoringConfigurationContext();

  const stepStateCtx = useStepStateContext();

  const groupItemGrades = useGroupItemGrades({ groupId, item });

  return (
    <GraderModuleSaveButton
      {...props}
      createProctorConfigOverride={createProctorConfigOverride}
      programmingCreateAttemptOverride={programmingCreateAttemptOverride}
      programmingProctorConfigContext={programmingProctorConfigContext}
      stepStateCtx={stepStateCtx}
      groupItemGrades={groupItemGrades.groupItemGrades}
      refetchGroupItemGrades={groupItemGrades.refetchGroupItemGrades}
    />
  );
};

export default compose<Props, InputProps>(
  connectToStores<PropsFromStores, InputProps>(
    ['SessionsAuthoringSessionsStore', 'SessionItemStore', 'ProctorConfigStore', 'TeachAppStore'],
    ({ SessionsAuthoringSessionsStore, SessionItemStore, ProctorConfigStore, TeachAppStore }, props) => {
      return {
        courseId: TeachAppStore.getCourse()?.id,
        modifiedSession:
          props.sessionItem && SessionsAuthoringSessionsStore.getModifiedSessionById(props.sessionItem.sessionId),
        hasSessionSaveError: SessionsAuthoringSessionsStore.getHasSessionSaveError(),
        hasValidSessionDates:
          props.sessionItem &&
          SessionsAuthoringSessionsStore.getHasValidModuleDeadlines(props.sessionItem.sessionId) &&
          !SessionItemStore.hasInvalidDates(props.sessionItem.sessionId),
        isSessionSaveSuccess: SessionsAuthoringSessionsStore.getLearnerSessionSaveSuccess(props.userId),
        modifiedProctorConfig: ProctorConfigStore.hasChanges(),
        hasProctorConfigSaveError: ProctorConfigStore.hasErrors(),
        proctorConfigDraftId: ProctorConfigStore.getDraftId(),
        groupId: props.sessionItem.groupId,
      };
    }
  )
)(GraderModuleSaveButtonWrapper);
